import { memo } from 'react'

import { IAccessibility } from 'clickNgo/models/widget'
import { RenderWhen } from 'cng-common/components/renderWhen'
import { FormInput, useGetFormData } from 'cng-common/features/form'
import { FieldWrapperWithChanges } from 'cng-common/features/form/components'
import { ValidationType } from 'cng-common/features/form/configs'
import { wasFieldModified } from 'cng-common/features/form/helpers'
import { FieldGroup } from 'shared/components/fieldGroup'

import { ContactFormContent } from '../../config/forms'
import { TContactFormKeys } from '../../model/forms'
import {
  IContactGeneralFormHandlers,
  IContactGeneralFormRequiredFields,
  IContactGeneralFormValidation,
  TContactGeneralForm,
} from '../../model/forms/general'
import { IContact } from '../../model/types'

import { renderValidationMessages } from './helpers'

interface IProps
  extends IAccessibility,
    IContactGeneralFormHandlers,
    IContactGeneralFormValidation,
    IContactGeneralFormRequiredFields {
  contact: IContact
  formKeys: TContactFormKeys
  isCollapsible?: boolean
  isCollapsed?: boolean
  hasOwnWarning?: boolean
  validationType?: ValidationType
}

export const ContactGeneralInfo = memo<IProps>(
  (props) => {
    const [{ invalidFields, isValid }] = useGetFormData<TContactGeneralForm>(props.formKeys.general)

    return (
      <FieldGroup
        title={ContactFormContent.titles.general}
        size={4}
        isCollapsible={props.isCollapsible}
        isCollapsed={props.isCollapsed}
      >
        <RenderWhen condition={props.hasOwnWarning && !isValid}>
          {renderValidationMessages(invalidFields)}
        </RenderWhen>
        <FieldWrapperWithChanges
          hasChanges={wasFieldModified(
            props.contact.modifiedValues,
            ContactFormContent.fields.firstName.field
          )}
          isHidden={!props.isReadOnly}
        >
          <FormInput
            defaultValue={props.contact.firstName}
            field={ContactFormContent.fields.firstName.field}
            label={ContactFormContent.fields.firstName.label}
            formKey={props.formKeys.general}
            isRequired={props.isFirstNameRequired}
            isDisabled={props.isDisabled || props.isDisabledByRegion}
            isReadOnly={props.isReadOnly}
            onChange={props.onFirstNameChange}
            onValidate={props.onFirstNameValidate}
            validationType={props.validationType}
          />
        </FieldWrapperWithChanges>
        <FieldWrapperWithChanges
          hasChanges={wasFieldModified(
            props.contact.modifiedValues,
            ContactFormContent.fields.lastName.field
          )}
          isHidden={!props.isReadOnly}
        >
          <FormInput
            defaultValue={props.contact.lastName}
            field={ContactFormContent.fields.lastName.field}
            label={ContactFormContent.fields.lastName.label}
            formKey={props.formKeys.general}
            isRequired={props.isLastNameRequired}
            isDisabled={props.isDisabled || props.isDisabledByRegion}
            isReadOnly={props.isReadOnly}
            onChange={props.onLastNameChange}
            onValidate={props.onLastNameValidate}
            validationType={props.validationType}
          />
        </FieldWrapperWithChanges>
        <FieldWrapperWithChanges
          hasChanges={wasFieldModified(
            props.contact.modifiedValues,
            ContactFormContent.fields.email.field
          )}
          isHidden={!props.isReadOnly}
        >
          <FormInput
            defaultValue={props.contact.email}
            field={ContactFormContent.fields.email.field}
            label={ContactFormContent.fields.email.label}
            formKey={props.formKeys.general}
            isRequired={props.isEmailRequired}
            isDisabled={props.isDisabled || props.isDisabledByRegion}
            isReadOnly={props.isReadOnly}
            onChange={props.onEmailChange}
            onValidate={props.onEmailValidate}
            validationType={props.validationType}
          />
        </FieldWrapperWithChanges>
      </FieldGroup>
    )
  },
  (prev, next) =>
    prev.isReadOnly === next.isReadOnly &&
    prev.isDisabled === next.isDisabled &&
    prev.isDisabledByRegion === next.isDisabledByRegion &&
    prev.isVisible === next.isVisible &&
    prev.contact.contactId === next.contact.contactId &&
    prev.contact.firstName === next.contact.firstName &&
    prev.contact.lastName === next.contact.lastName &&
    prev.contact.email === next.contact.email &&
    JSON.stringify(prev.contact.modifiedValues) === JSON.stringify(next.contact.modifiedValues) &&
    prev.onEmailValidate === next.onEmailValidate
)

ContactGeneralInfo.displayName = 'ContactGeneralInfo'
